import { useEffect, useState } from "react";
import { MetricCard } from "../../components/layout/MetricCard";
import { Panel } from "../../components/layout/Panel";
import { WarningPanel } from "../../components/layout/WarningPanel";
import { apiFetch } from "../../lib/api";

type CreatorIntelligenceStatus = {
  intelligence_blend: number;
  sample_count: number;
  supplier_sample_count: number;
  feature_weights: Record<string, number>;
  consent?: {
    subject_email: string;
    allow_cross_customer_training: boolean;
    accepted_training_addendum: boolean;
  } | null;
  warnings?: string[];
};

type CreatorGlobalSnapshot = {
  eligible_subjects: number;
  rows: number;
  summary?: {
    feature_averages?: Record<string, number | null>;
    tier_mix?: Record<string, number>;
    outcome_mix?: Record<string, number>;
  };
  warnings?: string[];
};

export function CreatorReportsWorkspace() {
  const [status, setStatus] = useState<CreatorIntelligenceStatus | null>(null);
  const [snapshot, setSnapshot] = useState<CreatorGlobalSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiFetch<CreatorIntelligenceStatus>("/intelligence/status"),
      apiFetch<CreatorGlobalSnapshot>("/intelligence/global-snapshot"),
    ])
      .then(([statusPayload, snapshotPayload]) => {
        if (cancelled) return;
        setStatus(statusPayload);
        setSnapshot(snapshotPayload);
      })
      .catch((err) => {
        if (cancelled) return;
        setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Panel>
        <h2>Creator Intelligence Reports</h2>
        <p className="muted-small">
          Hidden creator-facing view for adaptive model telemetry, network learning readiness, and consent-backed
          training visibility. This data should stay out of normal customer workflows.
        </p>
      </Panel>

      <WarningPanel title="Creator Report Warnings" warnings={error ? [error] : []} />

      {status && (
        <Panel>
          <h3>Adaptive Intelligence Status</h3>
          <div className="metric-grid">
            <MetricCard label="Intelligence Blend" value={status.intelligence_blend.toFixed(3)} />
            <MetricCard label="Local Feedback Samples" value={status.sample_count} />
            <MetricCard label="Supplier Signals" value={status.supplier_sample_count} />
            <MetricCard
              label="Training Consent"
              value={status.consent?.allow_cross_customer_training ? "Enabled" : "Disabled"}
            />
          </div>
        </Panel>
      )}

      {snapshot && (
        <Panel>
          <h3>Global Snapshot</h3>
          <div className="metric-grid">
            <MetricCard label="Opted-in Subjects" value={snapshot.eligible_subjects} />
            <MetricCard label="Training Rows" value={snapshot.rows} />
          </div>
          <pre className="creator-report-pre">{JSON.stringify(snapshot.summary || {}, null, 2)}</pre>
        </Panel>
      )}
    </>
  );
}
